/**
 * Module Santé — client API.
 *
 * Enveloppe les endpoints DRF montés sous `/api/health/` (cf. backend
 * `Health/urls.py`). Toutes les requêtes sont authentifiées (token).
 *
 * Les listes DRF sont paginées (LimitOffsetPagination) : `unwrap` renvoie
 * directement `results` quand la réponse est paginée.
 */
import {
  makeGetRequest,
  makePostRequest,
  makePatchRequest,
  makePutRequest,
  makeDeleteRequest,
} from '../../../utils/api'

const BASE = '/api/health/'

const ok = (r) => r && r.status >= 200 && r.status < 300
const unwrap = (r) => {
  if (!ok(r)) return null
  const d = r.data
  if (d && typeof d === 'object' && Array.isArray(d.results)) return d.results
  return d
}

// CRUD générique sur une collection (`/api/health/<resource>/`).
const collection = (resource) => ({
  list: async (params = '') =>
    unwrap(await makeGetRequest(`${BASE}${resource}/${params}`, {}, true)) || [],
  get: async (id) =>
    unwrap(await makeGetRequest(`${BASE}${resource}/${id}/`, {}, true)),
  create: async (body) =>
    unwrap(await makePostRequest(`${BASE}${resource}/`, body, {}, true)),
  update: async (id, body) =>
    unwrap(await makePatchRequest(`${BASE}${resource}/${id}/`, body, {}, true)),
  remove: async (id) => ok(await makeDeleteRequest(`${BASE}${resource}/${id}/`, {}, true)),
})

export const santeApi = {
  // --- 3.2 Onboarding ---
  getProfile: async () => unwrap(await makeGetRequest(`${BASE}profile/`, {}, true)),
  updateProfile: async (body) =>
    unwrap(await makePutRequest(`${BASE}profile/`, body, {}, true)),
  completeOnboarding: async () =>
    unwrap(await makePostRequest(`${BASE}onboarding/complete/`, {}, {}, true)),
  getOverview: async () => unwrap(await makeGetRequest(`${BASE}overview/`, {}, true)),

  goals: collection('goals'),
  consents: collection('consents'),
  sources: collection('sources'),

  // --- 3.3 Hub de données ---
  events: collection('events'),
  ingest: async (source, events) =>
    unwrap(await makePostRequest(`${BASE}events/ingest/`, { source, events }, {}, true)),

  // --- 3.4 Tableau de bord ---
  getDashboardToday: async () =>
    unwrap(await makeGetRequest(`${BASE}dashboard/today/`, {}, true)),
  recomputeScore: async (date) =>
    unwrap(await makePostRequest(`${BASE}dashboard/recompute-score/`, { date }, {}, true)),
  getTrends: async (metric = 'steps', days = 30) =>
    unwrap(
      await makeGetRequest(`${BASE}trends/?metric=${metric}&days=${days}`, {}, true),
    ),
  generateWeeklyReview: async () =>
    unwrap(await makePostRequest(`${BASE}weekly-review/generate/`, {}, {}, true)),
  weeklyReviews: collection('weekly-reviews'),

  // --- 3.5 Dossier médical ---
  conditions: collection('conditions'),
  addictions: collection('addictions'),
  familyHistory: collection('family-history'),
  treatments: collection('treatments'),
  vaccinations: collection('vaccinations'),
  appointments: collection('appointments'),
  labResults: collection('lab-results'),
  documents: collection('documents'),
  processDocument: async (id) =>
    unwrap(await makePostRequest(`${BASE}documents/${id}/process/`, {}, {}, true)),
  validateDocument: async (id, extractedData) =>
    unwrap(
      await makePostRequest(
        `${BASE}documents/${id}/validate_extraction/`,
        { extracted_data: extractedData },
        {},
        true,
      ),
    ),

  // --- 3.6 IA Coach & Insights ---
  insights: collection('insights'),
  generateDailySynthesis: async () =>
    unwrap(await makePostRequest(`${BASE}insights/daily-synthesis/`, {}, {}, true)),
  detectCorrelations: async () =>
    unwrap(await makePostRequest(`${BASE}insights/correlations/`, {}, {}, true)),
  actionPlans: collection('action-plans'),
  alerts: collection('alerts'),
  acknowledgeAlert: async (id) =>
    unwrap(await makePostRequest(`${BASE}alerts/${id}/acknowledge/`, {}, {}, true)),
  runRules: async () =>
    unwrap(await makePostRequest(`${BASE}rules/run/`, {}, {}, true)),
  conversations: collection('conversations'),
  askCoach: async (conversationId, message) =>
    unwrap(
      await makePostRequest(
        `${BASE}conversations/${conversationId}/ask/`,
        { message },
        {},
        true,
      ),
    ),
  safetyCheck: async (text) =>
    unwrap(await makePostRequest(`${BASE}safety/check/`, { text }, {}, true)),

  // --- 3.7 Notifications & rappels ---
  reminders: collection('reminders'),
  logReminder: async (id, status, note = '') =>
    unwrap(
      await makePostRequest(`${BASE}reminders/${id}/log/`, { status, note }, {}, true),
    ),
  notifications: collection('notifications'),

  // --- 3.8 Export & partage ---
  exports: collection('exports'),
  requestExport: async (format = 'full') =>
    unwrap(await makePostRequest(`${BASE}exports/`, { export_format: format }, {}, true)),
  generateConsultationReport: async (appointment) =>
    unwrap(
      await makePostRequest(
        `${BASE}consultation-report/generate/`,
        { appointment },
        {},
        true,
      ),
    ),
  getAccountDeletion: async () =>
    unwrap(await makeGetRequest(`${BASE}account/deletion/`, {}, true)),
  requestAccountDeletion: async () =>
    unwrap(await makePostRequest(`${BASE}account/deletion/`, {}, {}, true)),
  cancelAccountDeletion: async () =>
    unwrap(await makePostRequest(`${BASE}account/deletion/cancel/`, {}, {}, true)),

  // --- 6.x Journal d'accès ---
  accessLogs: collection('access-logs'),
}

export default santeApi
